"use client"

import { useState, useEffect } from "react"
import { Plus, Edit2, Trash2, Check, X, BookOpen, Sparkles } from "lucide-react"
import { createGothram, getGothrams, updateGothram, deleteGothram as apiDeleteGothram } from "../utils/api"

export default function Gothram() {
  const [gothrams, setGothrams] = useState([])
  const [newGothram, setNewGothram] = useState("")
  const [fetching, setFetching] = useState(true)
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState(null)
  // Track per-item actions
  const [editingId, setEditingId] = useState(null)
  const [editingName, setEditingName] = useState("")
  const [savingId, setSavingId] = useState(null)
  const [deletingId, setDeletingId] = useState(null)

  // Fetch gothrams on mount
  useEffect(() => {
    const fetchGothrams = async () => {
      try {
        setFetching(true)
        const res = await getGothrams()
        const formatted = res.data.map((g) => ({ ...g, id: g._id }))
        setGothrams(formatted)
      } catch (err) {
        setError("Failed to fetch gothrams")
        console.error(err)
      } finally {
        setFetching(false)
      }
    }
    fetchGothrams()
  }, [])

  const addGothram = async () => {
    if (!newGothram.trim()) return
    try {
      setAdding(true)
      const res = await createGothram({ name: newGothram.trim() })
      setGothrams((prev) => [...prev, { ...res.data, id: res.data._id }])
      setNewGothram("")
    } catch (err) {
      setError("Failed to add gothram")
      console.error(err)
    } finally {
      setAdding(false)
    }
  }

  const startEditing = (gothram) => {
    setEditingId(gothram.id)
    setEditingName(gothram.name)
  }

  const saveEdit = async () => {
    if (!editingName.trim()) return
    try {
      setSavingId(editingId)
      await updateGothram(editingId, { name: editingName.trim() })
      setGothrams((prev) => prev.map((g) => (g.id === editingId ? { ...g, name: editingName.trim() } : g)))
      cancelEdit()
    } catch (err) {
      setError("Failed to update gothram")
      console.error(err)
    } finally {
      setSavingId(null)
    }
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditingName("")
  }

  const deleteGothram = async (id) => {
    try {
      setDeletingId(id)
      await apiDeleteGothram(id)
      setGothrams((prev) => prev.filter((g) => g.id !== id))
    } catch (err) {
      setError("Failed to delete gothram")
      console.error(err)
    } finally {
      setDeletingId(null)
    }
  }

  const handleKeyPress = (e, action) => e.key === "Enter" && action()

  if (fetching) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cyan-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mb-4"></div>
          <p className="text-lg font-semibold text-gray-700">Loading gothrams...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cyan-50 to-blue-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <X className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Gothrams</h3>
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cyan-50 to-blue-50 py-8 px-4">
      <div className="w-full mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-xl shadow-lg">
              <BookOpen className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
              Gothram Management
            </h1>
          </div>
          <p className="text-gray-600 text-lg">Manage various gothrams (lineages in Hindu tradition)</p>
        </div>

        {/* Add Gothram Section */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-green-600 to-teal-600 p-6 text-white">
            <div className="flex items-center gap-2">
              <Plus className="h-6 w-6" />
              <h2 className="text-xl font-semibold">Add New Gothram</h2>
            </div>
          </div>
          <div className="p-6">
            <div className="flex gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Enter gothram name..."
                  value={newGothram}
                  onChange={(e) => setNewGothram(e.target.value)}
                  onKeyPress={(e) => handleKeyPress(e, addGothram)}
                  className="w-full h-12 px-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg"
                  disabled={adding}
                />
              </div>
              <button
                onClick={addGothram}
                disabled={!newGothram.trim() || adding}
                className="h-12 px-8 bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white rounded-xl font-semibold disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg hover:shadow-xl transition-all duration-200"
              >
                {adding ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5" />
                    Add Gothram
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Gothrams List */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-6 w-6" />
                <h2 className="text-xl font-semibold">Gothrams List</h2>
              </div>
              <div className="bg-white/20 px-4 py-2 rounded-full">
                <span className="font-semibold">
                  {gothrams.length} {gothrams.length === 1 ? "gothram" : "gothrams"}
                </span>
              </div>
            </div>
          </div>

          <div className="p-6">
            {gothrams.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-24 h-24 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <BookOpen className="w-12 h-12 text-purple-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Gothrams Added Yet</h3>
                <p className="text-gray-600">Start by adding your first gothram above!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {gothrams.map((gothram, idx) => (
                  <div
                    key={gothram.id}
                    className="group bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl p-4 transition-all duration-200 hover:shadow-md"
                  >
                    {editingId === gothram.id ? (
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg">
                          <span className="text-purple-600 font-bold text-sm">{String(idx + 1).padStart(2, "0")}</span>
                        </div>
                        <input
                          type="text"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onKeyPress={(e) => handleKeyPress(e, saveEdit)}
                          className="flex-1 h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          autoFocus
                          disabled={savingId === gothram.id}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={saveEdit}
                            disabled={!editingName.trim() || savingId === gothram.id}
                            className="h-10 px-4 bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white rounded-lg font-medium disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed flex items-center gap-2 shadow-md hover:shadow-lg transition-all duration-200"
                          >
                            {savingId === gothram.id ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Saving...
                              </>
                            ) : (
                              <>
                                <Check className="w-4 h-4" />
                                Save
                              </>
                            )}
                          </button>
                          <button
                            onClick={cancelEdit}
                            disabled={savingId === gothram.id}
                            className="h-10 px-4 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white rounded-lg font-medium flex items-center gap-2 shadow-md hover:shadow-lg transition-all duration-200"
                          >
                            <X className="w-4 h-4" />
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg">
                            <span className="text-purple-600 font-bold text-sm">
                              {String(idx + 1).padStart(2, "0")}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full flex items-center justify-center">
                              <BookOpen className="w-3 h-3 text-white" />
                            </div>
                            <span className="font-semibold text-gray-900 text-lg">{gothram.name}</span>
                          </div>
                        </div>
                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <button
                            onClick={() => startEditing(gothram)}
                            disabled={deletingId === gothram.id}
                            className="h-10 px-4 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white rounded-lg font-medium flex items-center gap-2 shadow-md hover:shadow-lg transition-all duration-200"
                          >
                            <Edit2 className="w-4 h-4" />
                            Edit
                          </button>
                          <button
                            onClick={() => deleteGothram(gothram.id)}
                            disabled={deletingId === gothram.id}
                            className="h-10 px-4 bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white rounded-lg font-medium flex items-center gap-2 shadow-md hover:shadow-lg transition-all duration-200"
                          >
                            {deletingId === gothram.id ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Deleting...
                              </>
                            ) : (
                              <>
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}